/* QualifyCvesOptionsRenderer.js */


const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const CheckboxControl = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;

const DEEPENING_OPTIONS = [
    {label: "Default (org setting)", value: "default"},
    {label: "Off", value: "off"},
    {label: "On-demand", value: "on-demand"},
];

class QualifyCvesOptionsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("qualify-cves-options-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        // Default ON: the launch routes through the exposureRefresh orchestrator,
        // which only refreshes dependencies that are missing or past their org
        // freshness windows. Unchecking scores against the data already on record.
        this._refreshDepsCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Refresh stale dependencies first (capture, taxonomy, support data)",
            value: true,
        });
        this._reextractCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Re-extract failed predicates (retry failed LLM extraction)",
            value: false,
        });
        // SEPARATE from the option above, deliberately. This one rewrites predicates that already
        // succeeded, on advisories whose text has not changed — and LLM extraction is not
        // deterministic. A measured run re-read 64 unchanged advisories and moved 8 verdicts, every
        // one from Inconclusive to Not Affected, because the re-read returned fewer required
        // feature tags. The label has to say that, because the operator otherwise sees verdicts
        // move and has no way to connect it to their own checkbox.
        this._reextractAllCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Re-extract ALL predicates — rewrites working rules; verdicts may move",
            value: false,
        });
        this._forceRecomputeCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Force recompute (ignore unchanged device signature)",
            value: false,
        });

        this._deepeningContainer = document.createElement("div");
        this._deepeningContainer.classList.add("field");
        this._deepeningLabel = document.createElement("label");
        this._deepeningLabel.innerText = "Runtime deepening";
        // Single-select with no clear affordance, so the effective default
        // ("Default (org setting)") must render preselected.
        this._deepeningCombo = new ComboBoxControl({
            options: DEEPENING_OPTIONS,
            value: "default",
        });
        this._deepeningHint = document.createElement("span");
        this._deepeningHint.classList.add("hint");
        this._deepeningHint.innerText =
            "On-demand may run live device commands and is ignored without device-execution privilege.";

        this._cveIdsContainer = document.createElement("div");
        this._cveIdsContainer.classList.add("field");
        this._cveIdsLabel = document.createElement("label");
        this._cveIdsLabel.innerText = "Limit to CVE IDs (comma-separated, optional)";
        this._cveIdsInput = new InputTextControl({
            darkTheme: true,
            placeholder: "e.g. CVE-2025-20169, CVE-2025-20352",
        });

        this._advisoryIdsContainer = document.createElement("div");
        this._advisoryIdsContainer.classList.add("field");
        this._advisoryIdsLabel = document.createElement("label");
        this._advisoryIdsLabel.innerText = "Limit to advisory IDs (comma-separated, optional)";
        this._advisoryIdsInput = new InputTextControl({
            darkTheme: true,
            placeholder: "e.g. cisco-sa-wlc-dos-nhX5KGOm",
        });

        this._buttonContainer = document.createElement("button-container");
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });
        this._launchButton = new ButtonControl({
            label: "Run",
            darkTheme: true,
            type: "additive",
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._launchButton.on(
            "UserClick",
            (this._onLaunchButtonClick = this._onLaunchButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._launchButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        const promArr = [
            this._refreshDepsCheckbox.shutdown(),
            this._reextractCheckbox.shutdown(),
            this._reextractAllCheckbox.shutdown(),
            this._forceRecomputeCheckbox.shutdown(),
            this._deepeningCombo.shutdown(),
            this._cveIdsInput.shutdown(),
            this._advisoryIdsInput.shutdown(),
            this._cancelButton.shutdown(),
            this._launchButton.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._refreshDepsCheckbox = undefined;
                this._reextractCheckbox = undefined;
                this._reextractAllCheckbox = undefined;
                this._forceRecomputeCheckbox = undefined;
                this._deepeningLabel.remove();
                this._deepeningLabel = undefined;
                this._deepeningHint.remove();
                this._deepeningHint = undefined;
                this._deepeningCombo = undefined;
                this._deepeningContainer.remove();
                this._deepeningContainer = undefined;
                this._cveIdsLabel.remove();
                this._cveIdsLabel = undefined;
                this._cveIdsInput = undefined;
                this._cveIdsContainer.remove();
                this._cveIdsContainer = undefined;
                this._advisoryIdsLabel.remove();
                this._advisoryIdsLabel = undefined;
                this._advisoryIdsInput = undefined;
                this._advisoryIdsContainer.remove();
                this._advisoryIdsContainer = undefined;
                this._cancelButton = undefined;
                this._launchButton = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._model.logger.warn(
                    {error: err},
                    "QualifyCvesOptionsRenderer.shutdown() error"
                );
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished
     * rendering. If a non Promise is returned the caller will assume the Renderer has completed
     * rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(this._refreshDepsCheckbox.renderer.rootElement);
                this._rootElem.appendChild(this._reextractCheckbox.renderer.rootElement);
                this._rootElem.appendChild(this._reextractAllCheckbox.renderer.rootElement);
                this._rootElem.appendChild(this._forceRecomputeCheckbox.renderer.rootElement);

                this._deepeningContainer.appendChild(this._deepeningLabel);
                this._deepeningContainer.appendChild(this._deepeningCombo.renderer.rootElement);
                this._deepeningContainer.appendChild(this._deepeningHint);
                this._rootElem.appendChild(this._deepeningContainer);

                this._cveIdsContainer.appendChild(this._cveIdsLabel);
                this._cveIdsContainer.appendChild(this._cveIdsInput.renderer.rootElement);
                this._rootElem.appendChild(this._cveIdsContainer);

                this._advisoryIdsContainer.appendChild(this._advisoryIdsLabel);
                this._advisoryIdsContainer.appendChild(this._advisoryIdsInput.renderer.rootElement);
                this._rootElem.appendChild(this._advisoryIdsContainer);

                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._launchButton.renderer.rootElement);
                this._rootElem.appendChild(this._buttonContainer);
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getValues() {
        return {
            refreshDependencies: this._refreshDepsCheckbox.value,
            reextractPredicates: this._reextractCheckbox.value,
            reextractAllPredicates: this._reextractAllCheckbox.value,
            forceRecompute: this._forceRecomputeCheckbox.value,
            runtimeDeepening: this._deepeningCombo.getValue(),
            cveIds: this._cveIdsInput.value,
            advisoryIds: this._advisoryIdsInput.value,
        };
    }

    _onLaunchButtonClick() {
        this._model.launch(this._getValues());
    }

    _onCancelButtonClick() {
        const err = new Error("user rejected");
        err.userRejected = true;
        this._model.cancel(err);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = QualifyCvesOptionsRenderer;
